import { useEffect, useId, useState } from 'react';
import {
  ArrowRight,
  CaretDown,
  Check,
  Envelope,
  IconContext,
  List,
  Phone,
  Printer,
  X,
} from '@phosphor-icons/react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

/* ------------------------------------------------------------------ *
 * Brand constants (Lunara: four colours, plus two paper tints)
 * Limestone #F5F1E8 · Umber Ink #2B2722 · Stone #A39A8A · Moss #55603F
 * Paper #FBF9F6 and Sand #EAE5D9 are neutral tints of Limestone used
 * only to separate surfaces on screen. No pure white, no gradients.
 * ------------------------------------------------------------------ */

type Stream = 'hotels' | 'restaurants';

// Wordmark lockup. Decorative: the accessible name lives on the link.
function LunaraLogo({
  size = 140,
  className = '',
  reverse = false,
}: {
  size?: number;
  className?: string;
  reverse?: boolean;
}) {
  const textColor = reverse ? '#F5F1E8' : '#2B2722';
  const accentColor = reverse ? '#F5F1E8' : '#55603F';

  return (
    <svg
      width={size}
      viewBox="0 0 160 62"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={`select-none ${className}`}
    >
      <circle cx="48" cy="16" r="6" stroke={textColor} strokeWidth="1.2" fill="none" />
      <circle cx="80" cy="16" r="6" stroke={textColor} strokeWidth="1.2" fill="none" />
      <path d="M 80 10 A 6 6 0 0 1 80 22 Z" fill={textColor} />
      <circle cx="112" cy="16" r="6" stroke={accentColor} strokeWidth="1.2" fill={accentColor} />
      <text
        x="82.5"
        y="48"
        textAnchor="middle"
        fontFamily="'EB Garamond', Georgia, serif"
        fontSize="19"
        letterSpacing="0.38em"
        fill={textColor}
        fontWeight="400"
      >
        LUNARA
      </text>
    </svg>
  );
}

// Three moon phases: new, half, full.
function MoonPhases({
  size = 24,
  className = '',
  reverse = false,
}: {
  size?: number;
  className?: string;
  reverse?: boolean;
}) {
  const primaryColor = reverse ? '#F5F1E8' : '#2B2722';
  const accentColor = reverse ? '#F5F1E8' : '#55603F';

  return (
    <svg
      width={size * 4.5}
      height={size}
      viewBox="0 0 110 24"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="10" stroke={primaryColor} strokeWidth="1.2" fill="none" />
      <g transform="translate(43, 0)">
        <circle cx="12" cy="12" r="10" stroke={primaryColor} strokeWidth="1.2" fill="none" />
        <path d="M 12 2 A 10 10 0 0 1 12 22 Z" fill={primaryColor} />
      </g>
      <g transform="translate(86, 0)">
        <circle cx="12" cy="12" r="10" fill={accentColor} />
      </g>
    </svg>
  );
}

const NAV_LINKS = [
  { href: '#proposition', label: 'The Proposition' },
  { href: '#convictions', label: 'Our Convictions' },
  { href: '#journey', label: 'The Guest Journey' },
  { href: '#self-audit', label: 'Sensory Assessment' },
  { href: '#faq', label: 'Client Q&A' },
  { href: '#invitation', label: 'Begin a Conversation' },
] as const;

const AUDIT_METRICS: Record<
  Stream,
  { id: number; label: string; desc: string; sense: string }[]
> = {
  hotels: [
    { id: 1, label: 'Threshold Scent', desc: 'Aerosols or synthetic sprays in place of botanical, cold-pressed oils chosen for this place.', sense: 'Scent' },
    { id: 2, label: 'Acoustic Envelope', desc: 'Corridor echo, or a background rumble in public rooms above a peaceful 45 decibels.', sense: 'Sound' },
    { id: 3, label: 'Tactile Finish', desc: 'Standard hospitality sheet blends rather than linen a guest notices on the second touch.', sense: 'Touch' },
    { id: 4, label: 'Arrival Choreography', desc: 'Guests stand at a tall counter instead of being seated with something warm in hand.', sense: 'Ritual' },
    { id: 5, label: 'Overhead Light', desc: 'Cool-toned downlights casting hard shadows instead of low, warm, indirect light.', sense: 'Sight' },
    { id: 6, label: 'Acoustic Absorption', desc: 'Nothing in the public rooms or the bar to take the edge off a full house.', sense: 'Sound' },
    { id: 7, label: 'Materials of Place', desc: 'Composite board where local stone, regional timber or native material could sit.', sense: 'Provenance' },
    { id: 8, label: 'Welcome Pour', desc: 'Generic tea, bottled water or sweet juice instead of a seasonal botanical infusion.', sense: 'Taste' },
    { id: 9, label: 'Wayfinding Hush', desc: 'Loud or lit signage instead of brass, recessed stone and a layout that reads itself.', sense: 'Sight' },
    { id: 10, label: 'Air in the Room', desc: 'Audible air-conditioning drafting onto the bed instead of quiet, indirect ventilation.', sense: 'Touch' },
  ],
  restaurants: [
    { id: 1, label: 'Acoustic Bounce', desc: 'Hard surfaces build a room roar, and guests raise their voices to be heard across the table.', sense: 'Sound' },
    { id: 2, label: 'Menus Without Lineage', desc: 'Interchangeable global luxury vocabulary in place of the farm, the season, the grower.', sense: 'Provenance' },
    { id: 3, label: 'Weight of the Plate', desc: 'Thin, mass-produced ceramic instead of clay with weight in the hand.', sense: 'Touch' },
    { id: 4, label: 'Air Flow', desc: 'Extraction or air-conditioning carrying kitchen air across guest seating.', sense: 'Scent' },
    { id: 5, label: 'The Soft Welcome', desc: 'A rushed greeting at a lectern instead of an unhurried pause with a warm tonic.', sense: 'Ritual' },
    { id: 6, label: 'Glassware', desc: 'Standard commercial glassware where hand-blown, weighted crystal would be felt.', sense: 'Touch' },
    { id: 7, label: 'Light on the Table', desc: 'Flat, high-angle light washing out the table instead of a narrow, warm beam.', sense: 'Sight' },
    { id: 8, label: 'Table Rhythm', desc: 'Service arrives on fixed intervals rather than reading posture and the room.', sense: 'Ritual' },
    { id: 9, label: 'Cellar Temperature', desc: 'One storage temperature for a list that asks for several.', sense: 'Taste' },
    { id: 10, label: 'The Handover', desc: 'The bill in a synthetic folder rather than an unhurried close in paper or leather.', sense: 'Touch' },
  ],
};

const FAQS = [
  {
    q: 'What does the visit involve?',
    a: 'Nicholas Wong books and spends an evening as an ordinary paying guest — no notice, no VIP flag, no special treatment. That is the only way to read the operation as a guest meets it: the tempo of service, the light as it changes, the sound of a full room. Afterwards we share what we found and discuss whether an audit makes sense. There is no charge and no obligation.',
  },
  {
    q: 'Do you replace our designers or our operations team?',
    a: 'No. We work alongside them. Designers hold the space and the layout; operations holds the team and the volume. Lunara holds the sensory line across both — acoustics, linen, light schedules, scent at the threshold — so the experience supports the rate rather than working against it.',
  },
  {
    q: 'How is the value shown to an owner or a finance director?',
    a: 'Anything that cannot be counted rarely survives a budget cycle. Each change is mapped to something the operation already measures: repeat stays and repeat covers, the share of direct bookings, beverage attachment, and the reviews that name the atmosphere as the reason for coming. We frame results in retention, reputation and rate integrity.',
  },
  {
    q: 'Which regions do you serve?',
    a: 'Singapore first, where we can be on the floor beside the team. Beyond that we take on selected coastal resorts, heritage stays and private dining rooms elsewhere, holding no more than three active audits at any time.',
  },
];

// Photography sits edge to edge, unrounded, with the caption beneath it —
// never text across the image.
function Plate({
  src,
  alt,
  caption,
  width,
  height,
  band = 'h-[40vh] min-h-[260px] max-h-[520px]',
  eager = false,
}: {
  src: string;
  alt: string;
  caption: string;
  width: number;
  height: number;
  band?: string;
  eager?: boolean;
}) {
  return (
    <figure className="print:hidden">
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={eager ? 'high' : 'auto'}
        className={`w-full object-cover ${band}`}
      />
      <figcaption className="mx-auto max-w-7xl px-6 py-4 text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70 md:px-12">
        {caption}
      </figcaption>
    </figure>
  );
}

export default function App() {
  const [stream, setStream] = useState<Stream>('hotels');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [journeyStep, setJourneyStep] = useState(0);
  const [auditForm, setAuditForm] = useState({
    propertyName: '',
    location: 'Singapore',
    checkedItems: [] as number[],
  });
  const [auditSubmitted, setAuditSubmitted] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const reduceMotion = useReducedMotion();
  const uid = useId();
  const propertyId = `${uid}-property`;
  const locationId = `${uid}-location`;

  // Reset the reading when the reader switches stream: the metrics differ.
  useEffect(() => {
    setAuditForm((prev) => ({ ...prev, checkedItems: [] }));
    setAuditSubmitted(false);
  }, [stream]);

  // Mobile drawer: lock scroll, close on Escape.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileMenuOpen]);

  const fade = reduceMotion
    ? {}
    : {
        initial: { opacity: 0 },
        whileInView: { opacity: 1 },
        viewport: { once: true, margin: '-80px' },
        transition: { duration: 0.6, ease: 'easeOut' as const },
      };

  const toggleAuditItem = (id: number) => {
    setAuditForm((prev) => ({
      ...prev,
      checkedItems: prev.checkedItems.includes(id)
        ? prev.checkedItems.filter((item) => item !== id)
        : [...prev.checkedItems, id],
    }));
  };

  const selectedCount = auditForm.checkedItems.length;
  const scorePct = Math.round(((10 - selectedCount) / 10) * 100);

  const getAuditReport = () => {
    if (selectedCount === 0) {
      return {
        level: 'Nothing to correct yet',
        summary:
          'On this reading, the common points of sameness are absent. Every coordinate is considered, threshold to departure.',
        note1:
          'Hold the material provenance you have. Local stone and timber earn their place; synthetics quietly undo them.',
        note2:
          'Keep the ceilings quiet and revisit team training each quarter — unhurried choreography is a habit, not a fitting.',
        note3:
          'Your rate is protected by distinction. There is no reason to discount a room a guest cannot find elsewhere.',
      };
    }
    if (selectedCount <= 3) {
      return {
        level: 'A few points of friction',
        summary:
          'The foundation is considered. A small number of elements introduce friction — guests are comfortable, but the sense of being somewhere particular is not yet complete.',
        note1:
          'Let local material carry the surfaces a guest touches. A composite shortcut in one place is felt everywhere.',
        note2:
          'Start with the points you marked. One audible draft, or one corporate cue at arrival, can undo an otherwise faultless evening.',
        note3:
          'These are quiet corrections. They protect direct bookings and hold the rate through a soft season.',
      };
    }
    return {
      level: 'Sameness is setting in',
      summary:
        'The hardware may be considered, but the feeling is being diluted — by workflows built for volume and by standards written for anywhere.',
      note1:
        'Look first at what guests touch daily: the linen, the plate, the handle. Natural material where synthetic sits now.',
      note2:
        'Then corridor sound and overhead light. Echo and hard downlight break the calm within seconds of arrival.',
      note3:
        'An operation in this bracket sells on market price rather than preference. Resolving three or four points restores the ground under the rate.',
    };
  };

  const report = getAuditReport();
  const isHotels = stream === 'hotels';

  const journeyPanels = [
    {
      kicker: '01: Sense of Place',
      nav: 'Sense of Place',
      navDesc: isHotels
        ? 'Finding what makes a property unrepeatable.'
        : 'Finding what makes a table unrepeatable.',
      title: isHotels
        ? 'A concept the whole operation can hold'
        : 'An argument against the interchangeable menu',
      body: isHotels
        ? 'We find what makes a property unrepeatable — its place, its materials, its rituals — and shape a concept the whole operation can hold.'
        : 'We find what makes a table unrepeatable — its produce, its provenance, its rituals — the locale on the plate. We argue against the interchangeable menu.',
      practice: isHotels
        ? 'The stone underfoot, the produce in season, the welcome that belongs only here.'
        : 'The produce in season, the dish only you serve, the welcome that could belong nowhere else.',
      deliverable: isHotels
        ? 'A sensory brand book: the property’s concept, materials, scent and sound, documented so every team holds the same line.'
        : 'A concept note: the restaurant’s story, its argument against the interchangeable menu, and the details that make it unrepeatable.',
    },
    {
      kicker: '02: The Guest Journey',
      nav: 'The Guest Journey',
      navDesc: isHotels
        ? 'Designing the five senses across the stay.'
        : 'Designing the meal as an arc.',
      title: isHotels ? 'Designing senses across the stay' : 'Designing the meal as an arc',
      body: isHotels
        ? 'We design the five senses across the stay — arrival to departure — so each moment is deliberate, and nothing is left to decoration.'
        : 'We design the meal as an arc — doorway to last pour — across sight, sound, scent, taste and touch. Front of house and pacing are part of the design, not decoration.',
      practice: isHotels
        ? 'Scent at the threshold, the right hush at dinner, linen that rewards a second touch.'
        : 'Bread at the door, the right hush at the peak, plateware that rewards a second touch.',
      deliverable: isHotels
        ? 'A guest-journey map: each sense specified from arrival to departure, so the stay runs true every night.'
        : 'A service-choreography map and a five-senses plan the kitchen and floor can hold — every sense specified, doorway to last pour.',
    },
    {
      kicker: '03: The Standard Holds',
      nav: 'The Standard Holds',
      navDesc: 'Writing sensory and service details down.',
      title: isHotels ? 'Writing the sensory details down' : 'Making the experience repeat',
      body: isHotels
        ? 'A brand that shows up only when the right people are on shift isn’t a brand yet. We write the sensory and service details down — so it repeats every night, with any team.'
        : 'A restaurant that shows up only when the right people are on shift isn’t a brand yet. We write the sensory and service details down — so it repeats every service, with any team.',
      practice: isHotels
        ? 'No more hero shifts — the experience holds, whoever is on that night.'
        : 'No more hero shifts — the experience holds, whoever is on the floor that night.',
      deliverable:
        'Operating standards: the sensory and service cues, written down — so the experience repeats without us.',
    },
  ];

  const panel = journeyPanels[journeyStep];

  return (
    <IconContext.Provider value={{ weight: 'thin', size: 16, mirrored: false }}>
    <div className="min-h-screen bg-[#F5F1E8] text-[#2B2722] font-sans antialiased selection:bg-[#55603F] selection:text-[#F5F1E8]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-60 focus:bg-[#2B2722] focus:px-4 focus:py-2 focus:text-[13px] focus:uppercase focus:tracking-widest focus:text-[#F5F1E8]"
      >
        Skip to content
      </a>

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[#A39A8A]/40 bg-[#F5F1E8] px-6 py-5 md:px-12 print:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <a
            href="#main"
            className="flex items-center focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#55603F]"
            aria-label="Lunara Consulting — home"
          >
            <LunaraLogo size={135} />
          </a>

          <nav aria-label="Primary" className="hidden items-center gap-x-8 lg:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="py-2 text-[13px] uppercase tracking-[0.18em] text-[#2B2722] transition-colors hover:text-[#55603F] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#55603F]"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3 lg:hidden">
            <a
              href="#self-audit"
              className="border border-[#55603F] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-[#55603F]"
            >
              Assessment
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="flex items-center gap-1.5 border border-[#A39A8A] px-3 py-1.5 text-[#2B2722] transition-colors hover:text-[#55603F] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#55603F]"
              aria-expanded={mobileMenuOpen}
              aria-label="Open menu"
            >
              <span className="text-[11px] uppercase tracking-[0.18em]">Menu</span>
              <List size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            initial={reduceMotion ? undefined : { opacity: 0 }}
            animate={reduceMotion ? undefined : { opacity: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="fixed inset-0 z-50 flex flex-col justify-between overflow-y-auto bg-[#F5F1E8] p-6"
          >
            <div className="flex items-center justify-between border-b border-[#A39A8A]/40 pb-4">
              <LunaraLogo size={120} />
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                autoFocus
                className="flex items-center gap-1.5 border border-[#A39A8A] px-3 py-1.5 text-[#2B2722] transition-colors hover:text-[#55603F] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#55603F]"
                aria-label="Close menu"
              >
                <span className="text-[11px] uppercase tracking-[0.18em]">Close</span>
                <X size={16} aria-hidden="true" />
              </button>
            </div>

            <nav
              aria-label="Primary, mobile"
              className="my-auto flex flex-col items-center justify-center gap-7 py-8 text-center"
            >
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-serif text-2xl tracking-wide text-[#2B2722] transition-colors hover:text-[#55603F]"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="space-y-1.5 border-t border-[#A39A8A]/40 pt-5 text-center">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#55603F]">
                Singapore · lunaraconsulting.sg
              </p>
              <p className="text-[11px] tracking-wider text-[#2B2722]/70">
                UEN 202534428W · +65 8053 9318
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main id="main">
        {/* Hero */}
        <motion.section
          initial={reduceMotion ? undefined : { opacity: 0 }}
          animate={reduceMotion ? undefined : { opacity: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="border-b border-[#A39A8A]/30 px-6 py-20 text-center md:py-32 print:hidden"
        >
          <div className="mx-auto max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-3">
              <span className="h-px w-8 bg-[#A39A8A]" />
              <MoonPhases size={14} />
              <span className="h-px w-8 bg-[#A39A8A]" />
            </div>

            <p className="mb-6 font-serif text-3xl uppercase tracking-[0.38em] text-[#2B2722] md:text-4xl">
              Lunara
            </p>

            <h1 className="mb-8 font-serif text-3xl leading-[1.2] text-[#2B2722] md:text-5xl">
              Quiet counsel for considered hospitality
            </h1>

            <p className="mx-auto mb-10 max-w-3xl font-serif text-xl italic text-[#2B2722] md:text-2xl">
              Luxury is felt, not announced.
            </p>

            <p className="mx-auto mb-12 max-w-3xl text-base leading-relaxed text-[#2B2722]/80 md:text-lg">
              Lunara is the quiet counsel behind considered hospitality — helping hotels,
              restaurants and experience-makers engage every sense,{' '}
              <span className="font-serif italic text-[#55603F]">deliberately</span>, not
              decoratively.
            </p>

            <div
              className="flex flex-col items-center justify-center gap-4 sm:flex-row"
              role="group"
              aria-label="Choose your focus"
            >
              <span className="text-[13px] uppercase tracking-[0.18em] text-[#2B2722]/70">
                Choose your focus
              </span>
              <div className="inline-flex border border-[#A39A8A]">
                <button
                  type="button"
                  onClick={() => setStream('hotels')}
                  aria-pressed={isHotels}
                  className={`px-6 py-2.5 text-[13px] uppercase tracking-[0.18em] transition-colors ${
                    isHotels
                      ? 'bg-[#2B2722] text-[#F5F1E8]'
                      : 'text-[#2B2722]/75 hover:text-[#2B2722]'
                  }`}
                >
                  Hotels
                </button>
                <button
                  type="button"
                  onClick={() => setStream('restaurants')}
                  aria-pressed={!isHotels}
                  className={`border-l border-[#A39A8A] px-6 py-2.5 text-[13px] uppercase tracking-[0.18em] transition-colors ${
                    !isHotels
                      ? 'bg-[#2B2722] text-[#F5F1E8]'
                      : 'text-[#2B2722]/75 hover:text-[#2B2722]'
                  }`}
                >
                  Restaurants
                </button>
              </div>
            </div>
          </div>
        </motion.section>

        <Plate
          src="/images/threshold.jpg"
          alt="A man stands in an open doorway looking into a candlelit dining room, the table already set."
          caption="The threshold · where the evening is decided"
          width={1455}
          height={1081}
          eager
        />

        {/* Problem & proposition */}
        <motion.section
          {...fade}
          id="proposition"
          className="mx-auto max-w-7xl scroll-mt-28 border-b border-[#A39A8A]/30 px-6 py-24 md:px-12 print:hidden"
        >
          <div className="grid items-start gap-16 md:grid-cols-2">
            <div>
              <span className="mb-3 block text-[13px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                The core problem
              </span>
              <h2 className="mb-8 font-serif text-3xl leading-tight text-[#2B2722] md:text-4xl">
                Most considered {isHotels ? 'hotels' : 'restaurants'} are forgettable.
              </h2>

              <div className="space-y-6 text-base leading-relaxed text-[#2B2722]/80">
                {isHotels ? (
                  <>
                    <p>
                      A guest checks out remembering the thread count, not the stay. When every
                      lobby smells the same and every playlist could belong anywhere, a property
                      becomes interchangeable — and interchangeability competes on price.
                    </p>
                    <p className="border-l border-[#55603F] py-1 pl-4 font-serif text-lg italic text-[#55603F]">
                      Sameness is the quiet enemy of rate.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      A guest leaves remembering the dish, not the evening. When every tasting menu
                      reads the same and every room sounds like anywhere, a restaurant becomes
                      interchangeable — and interchangeability competes on discount.
                    </p>
                    <p className="border-l border-[#55603F] py-1 pl-4 font-serif text-lg italic text-[#55603F]">
                      Sameness is the quiet enemy of the return.
                    </p>
                  </>
                )}
              </div>

              <div className="mt-12 border-t border-[#A39A8A]/40 pt-10">
                <span className="mb-2 block text-[13px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                  What it costs
                </span>
                <h3 className="mb-3 font-serif text-lg text-[#2B2722]">Forgettable is expensive</h3>
                <p className="mb-6 text-[13px] text-[#2B2722]/70">
                  The cost of sameness shows up where operators feel it most.
                </p>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:grid-cols-1 lg:grid-cols-3">
                  <div className="border-t border-[#A39A8A] bg-[#FBF9F6] p-5">
                    <span className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                      Discounting
                    </span>
                    <p className="text-[13px] leading-relaxed text-[#2B2722]/80">
                      {isHotels
                        ? 'Undifferentiated rooms sell on price, not preference.'
                        : 'Undifferentiated tables sell on price, not preference.'}
                    </p>
                  </div>
                  <div className="border-t border-[#A39A8A] bg-[#FBF9F6] p-5">
                    <span className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                      Churn
                    </span>
                    <p className="text-[13px] leading-relaxed text-[#2B2722]/80">
                      {isHotels
                        ? 'Guests who feel nothing don’t return — and don’t tell others.'
                        : 'Guests who feel nothing don’t come back — and don’t tell anyone.'}
                    </p>
                  </div>
                  <div className="border-t border-[#A39A8A] bg-[#FBF9F6] p-5">
                    <span className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                      {isHotels ? 'Rate erosion' : 'Spend erosion'}
                    </span>
                    <p className="text-[13px] leading-relaxed text-[#2B2722]/80">
                      {isHotels
                        ? 'With no reason to pay more, the rate drifts to the market floor.'
                        : 'With no reason to trade up, average spend drifts to the market floor.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-between bg-[#2B2722] p-8 text-[#F5F1E8] md:p-12">
              <div>
                <div className="mb-6 flex justify-center">
                  <MoonPhases size={18} reverse />
                </div>

                <span className="mb-4 block text-center text-[12px] uppercase tracking-[0.25em] text-[#A39A8A]">
                  The proposition
                </span>

                <p className="mb-8 text-center font-serif text-2xl leading-relaxed md:text-3xl">
                  Lunara is the quiet counsel behind considered hospitality — helping{' '}
                  {isHotels
                    ? 'hotels, restaurants and experience-makers'
                    : 'restaurants, bars and experience-makers'}{' '}
                  engage every sense, <span className="italic text-[#A39A8A]">deliberately</span>,
                  not decoratively.
                </p>

                <p className="mb-8 text-center font-serif text-sm italic text-[#A39A8A]">
                  Luxury is felt, not announced.
                </p>
              </div>

              <div className="mt-4 border-t border-[#A39A8A]/40 pt-8">
                <span className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-[#A39A8A]">
                  The difference
                </span>
                <h3 className="mb-3 font-serif text-xl text-[#F5F1E8]">
                  One advisor, not an agency
                </h3>
                <p className="mb-6 text-[13px] leading-relaxed text-[#F5F1E8]/85">
                  You work with the person who does the thinking — not an account team, not a
                  junior learning on your brief.
                </p>

                <div className="grid grid-cols-2 gap-6 border-t border-[#A39A8A]/25 pt-5">
                  <div>
                    <span className="mb-1 block text-[11px] uppercase tracking-[0.2em] text-[#A39A8A]">
                      We take on
                    </span>
                    <p className="text-[13px] leading-relaxed text-[#F5F1E8]/90">
                      {isHotels
                        ? 'A few properties a year, each given real attention.'
                        : 'A few restaurants a year, each given real attention.'}
                    </p>
                  </div>
                  <div>
                    <span className="mb-1 block text-[11px] uppercase tracking-[0.2em] text-[#A39A8A]">
                      We decline
                    </span>
                    <p className="text-[13px] leading-relaxed text-[#F5F1E8]/90">
                      {isHotels
                        ? 'Work we can’t improve, and rooms we can’t stand behind.'
                        : 'Work we can’t improve, and tables we can’t stand behind.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        <Plate
          src={isHotels ? '/images/stone-bath.jpg' : '/images/table-setting.jpg'}
          alt={
            isHotels
              ? 'Steam rising from a bath carved out of a single block of stone, a folded towel on the ledge beside it.'
              : 'A place setting on unbleached linen: a clay plate, worn cutlery, a sprig of herbs and one lit candle.'
          }
          caption={
            isHotels
              ? 'Stone, cut once · warmth a guest can feel before they touch it'
              : 'Clay, linen, one candle · the table before anyone sits'
          }
          width={1536}
          height={1024}
        />

        {/* Convictions */}
        <motion.section
          {...fade}
          id="convictions"
          className="scroll-mt-28 bg-[#2B2722] px-6 py-24 text-[#F5F1E8] md:px-12 print:hidden"
        >
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-20 max-w-3xl text-center">
              <span className="mb-3 block text-[13px] uppercase tracking-[0.25em] text-[#A39A8A]">
                How we think
              </span>
              <h2 className="mb-6 font-serif text-3xl md:text-5xl">
                Three convictions behind the work
              </h2>
              <div className="mx-auto h-px w-20 bg-[#55603F]" />
            </div>

            <div className="grid gap-10 md:grid-cols-3">
              {[
                {
                  numeral: 'I',
                  title: 'Sense of Place',
                  body: isHotels
                    ? 'Every property should speak its locale — its materials, produce and rituals.'
                    : 'Every restaurant should speak its locale — its produce, provenance and rituals.',
                  practice: isHotels
                    ? 'The stone underfoot, the produce in season, the welcome that belongs only here.'
                    : 'The produce in season, the dish only you serve, the welcome that could belong nowhere else.',
                },
                {
                  numeral: 'II',
                  title: 'The Five Senses',
                  body: isHotels
                    ? 'Memorable hospitality, designed across sight, sound, scent, taste and touch.'
                    : 'Memorable hospitality, composed across sight, sound, scent, taste and touch.',
                  practice: isHotels
                    ? 'Scent at the threshold, the right hush at dinner, linen that rewards a second touch.'
                    : 'Bread at the door, the right hush at the peak, plateware that rewards a second touch.',
                },
                {
                  numeral: 'III',
                  title: 'The Standard Holds',
                  body: isHotels
                    ? 'Excellence is written down, not left to chance — so it repeats every night, with any team.'
                    : 'Excellence is written down, not left to chance — so it repeats every service, with any team.',
                  practice: isHotels
                    ? 'No more hero shifts — the experience holds, whoever is on that night.'
                    : 'No more hero shifts — the experience holds, whoever is on the floor that night.',
                },
              ].map((card) => (
                <div
                  key={card.numeral}
                  className="border border-[#A39A8A]/30 p-8 transition-colors duration-300 hover:border-[#55603F]"
                >
                  <div className="mb-4 font-serif text-3xl text-[#55603F]">{card.numeral}</div>
                  <h3 className="mb-4 font-serif text-xl text-[#F5F1E8]">{card.title}</h3>
                  <p className="mb-6 text-[13px] leading-relaxed text-[#F5F1E8]/80">{card.body}</p>
                  <div className="border-l border-[#55603F] pl-4">
                    <span className="mb-1 block text-[11px] uppercase tracking-[0.2em] text-[#A39A8A]">
                      In practice
                    </span>
                    <p className="text-[13px] italic leading-relaxed text-[#F5F1E8]/85">
                      {card.practice}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Guest journey */}
        <motion.section
          {...fade}
          id="journey"
          className="mx-auto max-w-7xl scroll-mt-28 border-b border-[#A39A8A]/30 px-6 py-24 md:px-12 print:hidden"
        >
          <div className="mb-16 text-center">
            <span className="mb-3 block text-[13px] uppercase tracking-[0.2em] text-[#55603F]">
              What we do
            </span>
            <h2 className="font-serif text-3xl text-[#2B2722] md:text-4xl">
              The service arc and its deliverables
            </h2>
            <p className="mt-3 text-[13px] uppercase tracking-[0.18em] text-[#2B2722]/70">
              How we design the {isHotels ? 'stay' : 'meal'} from doorway to departure
            </p>
          </div>

          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="flex flex-col gap-4 lg:col-span-4" role="tablist" aria-label="Service arc">
              {journeyPanels.map((step, idx) => (
                <button
                  key={step.kicker}
                  type="button"
                  role="tab"
                  id={`${uid}-tab-${idx}`}
                  aria-selected={journeyStep === idx}
                  aria-controls={`${uid}-panel-${idx}`}
                  onClick={() => setJourneyStep(idx)}
                  className={`w-full border p-6 text-left transition-colors duration-300 ${
                    journeyStep === idx
                      ? 'border-[#55603F] bg-[#FBF9F6]'
                      : 'border-[#A39A8A]/40 hover:border-[#A39A8A]'
                  }`}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                      0{idx + 1}
                    </span>
                    {journeyStep === idx && <span className="h-2 w-2 bg-[#55603F]" />}
                  </div>
                  <h3 className="mb-1 font-serif text-lg text-[#2B2722]">{step.nav}</h3>
                  <p className="text-[13px] text-[#2B2722]/70">{step.navDesc}</p>
                </button>
              ))}
            </div>

            <div
              role="tabpanel"
              id={`${uid}-panel-${journeyStep}`}
              aria-labelledby={`${uid}-tab-${journeyStep}`}
              className="flex min-h-[380px] flex-col justify-between border border-[#A39A8A]/40 bg-[#FBF9F6] p-8 md:p-12 lg:col-span-8"
            >
              <div>
                <span className="mb-6 inline-block border border-[#55603F] px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[#55603F]">
                  {panel.kicker}
                </span>
                <h3 className="mb-4 font-serif text-2xl text-[#2B2722]">{panel.title}</h3>
                <p className="mb-6 font-serif text-base leading-relaxed text-[#2B2722]/85">
                  {panel.body}
                </p>
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="border-l border-[#A39A8A] bg-[#F5F1E8] p-5">
                    <h4 className="mb-1 text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                      In practice
                    </h4>
                    <p className="text-[13px] italic leading-relaxed text-[#2B2722]/80">
                      {panel.practice}
                    </p>
                  </div>
                  <div className="border-l border-[#55603F] bg-[#F5F1E8] p-5">
                    <h4 className="mb-1 text-[11px] uppercase tracking-[0.2em] text-[#55603F]">
                      The deliverable
                    </h4>
                    <p className="font-serif text-[13px] italic leading-relaxed text-[#2B2722]/85">
                      {panel.deliverable}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex items-center justify-between border-t border-[#A39A8A]/30 pt-6">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                  {panel.kicker}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setJourneyStep((prev) => (prev > 0 ? prev - 1 : 2))}
                    className="border border-[#A39A8A] px-3 py-1.5 text-[13px] transition-colors hover:border-[#2B2722]"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setJourneyStep((prev) => (prev < 2 ? prev + 1 : 0))}
                    className="bg-[#2B2722] px-3 py-1.5 text-[13px] text-[#F5F1E8] transition-colors hover:bg-[#55603F]"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* How we work */}
        <motion.section
          {...fade}
          className="border-b border-[#A39A8A]/30 bg-[#FAF8F5] px-6 py-24 md:px-12 print:hidden"
        >
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-16 max-w-3xl text-center">
              <span className="mb-3 block text-[13px] uppercase tracking-[0.25em] text-[#55603F]">
                How we work
              </span>
              <h2 className="font-serif text-3xl text-[#2B2722] md:text-4xl">
                The same care, whatever you take on
              </h2>
              <div className="mx-auto mt-5 h-px w-12 bg-[#55603F]" />
            </div>

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ['Evidence', 'We start from what we saw on your floor, not a template.'],
                ['Sense', 'We design sense by sense — where each earns its place, and where restraint does.'],
                ['Together', 'Built beside your team, so the work holds without us.'],
                ['Yours', 'The book, the map, the standards — yours to keep when we go.'],
              ].map(([label, body]) => (
                <div key={label} className="border-t border-[#A39A8A] bg-[#F5F1E8] p-6">
                  <span className="mb-4 block border-b border-[#A39A8A]/30 pb-2 text-[11px] uppercase tracking-[0.2em] text-[#55603F]">
                    {label}
                  </span>
                  <p className="font-serif text-[15px] italic leading-relaxed text-[#2B2722]/85">
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* How we begin */}
        <motion.section
          {...fade}
          className="border-b border-[#A39A8A]/30 bg-[#FAF8F5] px-6 py-24 md:px-12 print:hidden"
        >
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-16 max-w-3xl text-center">
              <span className="mb-3 block text-[13px] uppercase tracking-[0.25em] text-[#55603F]">
                How we begin
              </span>
              <h2 className="mb-3 font-serif text-3xl text-[#2B2722] md:text-4xl">
                A visit, a menu, your choice
              </h2>
              <p className="mt-2 text-[13px] uppercase tracking-[0.18em] text-[#2B2722]/70">
                The visit is complimentary. What follows is modular.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {[
                [
                  'The visit',
                  isHotels
                    ? 'An unannounced evening in your public spaces. We show you what we found — and what sameness is quietly costing you. No charge.'
                    : 'An unannounced evening in your dining room. We show you what we found — and what sameness is quietly costing you. No charge.',
                ],
                [
                  'The menu',
                  isHotels
                    ? 'A menu of pieces — the audit itself, a sensory brand book, a guest-journey map, operating standards, a signature scent — taken singly or in bundles.'
                    : 'A menu of pieces — the audit, a concept note, a guest-journey map, operating standards, a signature scent — taken singly or in bundles.',
                ],
                [
                  'Your choice',
                  isHotels
                    ? 'You take only what your property needs — a single piece, a bundle, or the full path. Some choose only to tend. You decide whether to begin.'
                    : 'You take only what your restaurant needs — a single piece, a bundle, or the full path. Some choose only to tend. You decide whether to begin.',
                ],
              ].map(([label, body]) => (
                <div key={label} className="border-t border-[#A39A8A] bg-[#F5F1E8] p-8">
                  <span className="mb-4 block border-b border-[#A39A8A]/30 pb-2 text-[11px] uppercase tracking-[0.2em] text-[#55603F]">
                    {label}
                  </span>
                  <p className="font-serif text-[15px] leading-relaxed text-[#2B2722]/85">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Self-assessment */}
        <motion.section
          {...fade}
          id="self-audit"
          className="mx-auto max-w-5xl scroll-mt-28 px-6 py-24 md:px-12"
        >
          <div className="mb-12 text-center print:hidden">
            <span className="mb-3 block text-[13px] uppercase tracking-[0.2em] text-[#55603F]">
              Self-assessment
            </span>
            <h2 className="mb-4 font-serif text-3xl text-[#2B2722] md:text-4xl">
              Sensory calibration
            </h2>
            <p className="mx-auto max-w-xl text-[15px] leading-relaxed text-[#2B2722]/80">
              Mark what you recognise in your own rooms. We will show you where the friction sits,
              and what it tends to cost.
            </p>
          </div>

          <img
            src="/images/linen-on-stone.jpg"
            alt="Rumpled unbleached linen resting on rough stone, a pale ceramic bowl behind it."
            width={1536}
            height={1024}
            loading="lazy"
            decoding="async"
            className="mb-10 h-[180px] w-full object-cover md:h-[220px] print:hidden"
          />

          <div className="border border-[#A39A8A]/50 bg-[#FBF9F6]">
            <div className="flex items-center justify-between bg-[#2B2722] p-6 text-[#F5F1E8] md:px-8 print:hidden">
              <span className="font-serif text-[15px] uppercase tracking-[0.2em]">
                Ten quiet coordinates
              </span>
              <MoonPhases size={14} reverse />
            </div>

            <div className="space-y-8 p-6 md:p-10 print:hidden">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label
                    htmlFor={propertyId}
                    className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70"
                  >
                    {isHotels ? 'Property name' : 'Restaurant name'}
                  </label>
                  <input
                    id={propertyId}
                    type="text"
                    value={auditForm.propertyName}
                    onChange={(e) => setAuditForm({ ...auditForm, propertyName: e.target.value })}
                    autoComplete="organization"
                    className="w-full border border-[#A39A8A] bg-[#F5F1E8] px-4 py-2.5 text-[15px] text-[#2B2722] focus:border-[#55603F] focus:outline-none"
                  />
                </div>
                <div>
                  <label
                    htmlFor={locationId}
                    className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70"
                  >
                    City
                  </label>
                  <input
                    id={locationId}
                    type="text"
                    value={auditForm.location}
                    onChange={(e) => setAuditForm({ ...auditForm, location: e.target.value })}
                    autoComplete="address-level2"
                    className="w-full border border-[#A39A8A] bg-[#F5F1E8] px-4 py-2.5 text-[15px] text-[#2B2722] focus:border-[#55603F] focus:outline-none"
                  />
                </div>
              </div>

              <fieldset>
                <legend className="mb-3 text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                  Mark anything present in your current guest journey
                </legend>
                <div className="grid gap-3 sm:grid-cols-2">
                  {AUDIT_METRICS[stream].map((item) => {
                    const isChecked = auditForm.checkedItems.includes(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        aria-pressed={isChecked}
                        onClick={() => toggleAuditItem(item.id)}
                        className={`flex items-start gap-3 border p-4 text-left transition-colors ${
                          isChecked
                            ? 'border-[#55603F] bg-[#F5F1E8]'
                            : 'border-[#A39A8A]/40 bg-[#F5F1E8]/50 hover:border-[#A39A8A]'
                        }`}
                      >
                        <span
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border ${
                            isChecked
                              ? 'border-[#55603F] bg-[#55603F] text-[#F5F1E8]'
                              : 'border-[#A39A8A]'
                          }`}
                          aria-hidden="true"
                        >
                          {isChecked && <Check size={14} weight="light" />}
                        </span>
                        <span className="flex flex-col gap-1">
                          <span className="flex flex-wrap items-baseline gap-2">
                            <span className="text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                              {item.sense}
                            </span>
                            <span className="font-serif text-[15px] text-[#2B2722]">
                              {item.label}
                            </span>
                          </span>
                          <span className="block text-[13px] leading-relaxed text-[#2B2722]/75">
                            {item.desc}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="flex flex-col items-center border-t border-[#A39A8A]/30 pt-6">
                <button
                  type="button"
                  onClick={() => setAuditSubmitted(true)}
                  className="inline-flex items-center gap-3 bg-[#2B2722] px-8 py-3.5 text-[13px] uppercase tracking-[0.2em] text-[#F5F1E8] transition-colors hover:bg-[#55603F]"
                >
                  <span>See the reading</span>
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              </div>
            </div>

            <div aria-live="polite">
              {auditSubmitted && (
                <div className="border-t border-[#A39A8A]/50 bg-[#FAF8F5] p-6 md:p-10 print:border-0 print:bg-transparent print:p-0">
                  <div
                    id="diagnostic-report"
                    className="mx-auto max-w-4xl border border-[#A39A8A]/50 bg-[#FBF9F6] p-6 text-[15px] leading-relaxed text-[#2B2722] md:p-8 print:border-0 print:p-0"
                  >
                    <div className="mb-6 flex items-center justify-between border-b border-[#A39A8A]/50 pb-4">
                      <div className="flex flex-col">
                        <span className="font-serif text-[13px] uppercase tracking-[0.2em] text-[#2B2722]">
                          Lunara Consulting
                        </span>
                        <span className="text-[11px] uppercase tracking-[0.18em] text-[#2B2722]/70">
                          A first sensory reading
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => window.print()}
                        className="inline-flex items-center gap-2 border border-[#55603F] px-3 py-1.5 text-[13px] text-[#55603F] transition-colors hover:bg-[#55603F] hover:text-[#F5F1E8] print:hidden"
                      >
                        <Printer size={16} aria-hidden="true" />
                        <span>Print</span>
                      </button>
                    </div>

                    <div className="mb-8 grid items-center gap-6 bg-[#2B2722] p-6 text-[#F5F1E8] sm:grid-cols-3">
                      <div className="text-center sm:text-left">
                        <span className="block text-[11px] uppercase tracking-[0.2em] text-[#A39A8A]">
                          Calm reading
                        </span>
                        <span className="font-serif text-3xl text-[#F5F1E8]">{scorePct}%</span>
                      </div>
                      <div className="border-t border-[#A39A8A]/40 pt-4 text-[13px] leading-relaxed text-[#F5F1E8]/85 sm:col-span-2 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0">
                        <span className="mb-1 block text-[11px] uppercase tracking-[0.2em] text-[#A39A8A]">
                          Where this sits
                        </span>
                        <span className="mb-1 block font-serif text-[15px] text-[#F5F1E8]">
                          {report.level}
                        </span>
                        <span>{report.summary}</span>
                      </div>
                    </div>

                    <div className="space-y-8">
                      {[
                        ['I', 'Sense of place', report.note1],
                        ['II', 'The five senses', report.note2],
                        ['III', 'Quiet value', report.note3],
                      ].map(([numeral, heading, body]) => (
                        <div key={numeral} className="space-y-2">
                          <div className="flex items-center gap-2 border-b border-[#A39A8A]/40 pb-1">
                            <span className="font-serif text-[13px] text-[#55603F]">{numeral}</span>
                            <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#2B2722]">
                              {heading}
                            </h3>
                          </div>
                          <p className="font-serif text-[15px] italic leading-relaxed text-[#2B2722]/85">
                            {body}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 flex flex-col justify-between gap-1 border-t border-[#A39A8A]/40 pt-6 text-[13px] italic text-[#2B2722]/70 md:flex-row md:items-center">
                      <span>
                        Prepared for {auditForm.propertyName || 'a considered partner'}
                        {auditForm.location ? ` · ${auditForm.location}` : ''}
                      </span>
                      <span className="font-serif not-italic">nicholas@lunaraconsulting.sg</span>
                    </div>
                  </div>

                  <div className="mt-10 space-y-4 text-center print:hidden">
                    <p className="mx-auto max-w-xl text-[15px] leading-relaxed text-[#2B2722]/80">
                      This is a first reading, not a verdict. If it rings true, the next step is a
                      visit — an unannounced evening in your spaces, at no charge. You are welcome
                      to begin there.
                    </p>
                    <a
                      href="#invitation"
                      className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.2em] text-[#55603F] transition-colors hover:text-[#2B2722]"
                    >
                      Ask Nicholas for the visit
                      <ArrowRight size={14} aria-hidden="true" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section
          {...fade}
          id="faq"
          className="scroll-mt-28 border-b border-[#A39A8A]/40 bg-[#EAE5D9] px-6 py-24 md:px-12 print:hidden"
        >
          <div className="mx-auto max-w-4xl">
            <div className="mb-16 text-center">
              <span className="mb-3 block text-[13px] uppercase tracking-[0.2em] text-[#55603F]">
                Understated rigour
              </span>
              <h2 className="font-serif text-3xl text-[#2B2722] md:text-4xl">
                Questions clients ask
              </h2>
              <p className="mt-3 text-[13px] uppercase tracking-[0.18em] text-[#2B2722]/70">
                The details that do not fit on a pitch slide
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {FAQS.map((faq, idx) => {
                const open = activeFaq === idx;
                return (
                  <div key={faq.q} className="border border-[#A39A8A]/50 bg-[#FBF9F6]">
                    <h3>
                      <button
                        type="button"
                        onClick={() => setActiveFaq(open ? null : idx)}
                        aria-expanded={open}
                        aria-controls={`${uid}-faq-${idx}`}
                        className="flex w-full items-center justify-between gap-4 p-6 text-left transition-colors hover:text-[#55603F]"
                      >
                        <span className="font-serif text-lg text-[#2B2722]">{faq.q}</span>
                        <CaretDown
                          size={18}
                          aria-hidden="true"
                          className={`shrink-0 text-[#55603F] transition-transform duration-300 ${
                            open ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    </h3>
                    {open && (
                      <div
                        id={`${uid}-faq-${idx}`}
                        className="border-t border-[#A39A8A]/30 px-6 pb-6 pt-4 text-[15px] leading-relaxed text-[#2B2722]/85"
                      >
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </motion.section>

        {/* The advisor */}
        <motion.section
          {...fade}
          className="border-b border-[#A39A8A]/40 bg-[#EAE5D9] px-6 py-24 md:px-12 print:hidden"
        >
          <div className="mx-auto grid max-w-5xl items-center gap-12 md:grid-cols-2">
            <div>
              <span className="mb-3 block text-[13px] uppercase tracking-[0.2em] text-[#55603F]">
                The advisor
              </span>
              <h2 className="mb-6 font-serif text-3xl text-[#2B2722] md:text-4xl">
                Rigour, brought to hospitality
              </h2>
              <p className="mb-6 text-[15px] leading-relaxed text-[#2B2722]/85">
                Nicholas Wong works where business strategy meets high-touch operations — where
                decisions answer to numbers, and detail is what separates one room from another.
              </p>
              <p className="mb-8 text-[15px] leading-relaxed text-[#2B2722]/85">
                Lunara turns that same attention to the physical environment: how a place feels, and
                what that feeling is worth to the operation.
              </p>
              <blockquote className="border-l border-[#55603F] py-1 pl-4 font-serif text-lg italic text-[#55603F]">
                We advise as owners, not vendors.
              </blockquote>
            </div>

            <div className="border border-[#A39A8A]/50 bg-[#FBF9F6] p-8 md:p-10">
              <h3 className="mb-6 border-b border-[#A39A8A]/40 pb-4 font-serif text-xl text-[#2B2722]">
                Practice overview
              </h3>
              <dl className="flex flex-col gap-6">
                {[
                  ['Background', 'Strategy and operations'],
                  ['Focus', isHotels ? 'Hotels, restaurants, experiences' : 'Restaurants, bars, experiences'],
                  ['How we engage', isHotels ? 'A few properties a year' : 'A few restaurants a year'],
                  ['Standard', 'Only work we can stand behind'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-[11px] uppercase tracking-[0.2em] text-[#2B2722]/70">
                      {label}
                    </dt>
                    <dd className="font-serif text-[15px] text-[#2B2722]">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </motion.section>

        <Plate
          src="/images/chair-and-tree.jpg"
          alt="A wooden chair beside a potted olive tree on a stone terrace at dusk, the sea beyond."
          caption="An olive tree, a chair, the last of the light · somewhere particular"
          width={1536}
          height={1024}
        />

        {/* Invitation */}
        <motion.section
          {...fade}
          id="invitation"
          className="scroll-mt-28 bg-[#2B2722] px-6 py-24 text-[#F5F1E8] md:px-12 print:hidden"
        >
          <div className="mx-auto max-w-4xl">
            <div className="mb-16 text-center">
              <MoonPhases size={20} reverse className="mx-auto mb-4" />
              <span className="mb-3 block text-[13px] uppercase tracking-[0.2em] text-[#A39A8A]">
                An invitation
              </span>
              <h2 className="font-serif text-3xl text-[#F5F1E8] md:text-5xl">
                Begin with a conversation
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-[#F5F1E8]/80">
                An unhurried exchange about what your rooms could become.
              </p>
            </div>

            <div className="mx-auto max-w-3xl border border-[#A39A8A]/30 p-8 text-center md:p-12">
              <p className="mb-2 text-[11px] uppercase tracking-[0.2em] text-[#A39A8A]">
                Direct contact
              </p>
              <p className="mb-1 font-serif text-xl text-[#F5F1E8]">Nicholas Wong</p>
              <p className="mb-8 text-[13px] uppercase tracking-[0.18em] text-[#A39A8A]">
                Founder and director · Lunara
              </p>

              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
                <a
                  href="mailto:nicholas@lunaraconsulting.sg"
                  className="flex w-full items-center justify-center gap-2 bg-[#F5F1E8] px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-[#2B2722] transition-colors duration-300 hover:bg-[#55603F] hover:text-[#F5F1E8] sm:w-auto"
                >
                  <Envelope size={16} aria-hidden="true" />
                  <span>Email Nicholas</span>
                </a>
                <a
                  href="tel:+6580539318"
                  className="flex w-full items-center justify-center gap-2 border border-[#A39A8A] px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-[#F5F1E8] transition-colors duration-300 hover:border-[#F5F1E8] sm:w-auto"
                >
                  <Phone size={16} aria-hidden="true" />
                  <span>Call +65 8053 9318</span>
                </a>
              </div>

              <p className="mt-8 text-[11px] uppercase tracking-[0.2em] text-[#A39A8A]">
                Based in Singapore · Available for selected properties abroad
              </p>
            </div>
          </div>
        </motion.section>
      </main>

      <footer className="border-t border-[#A39A8A]/40 bg-[#F5F1E8] px-6 py-12 text-[#2B2722]/75 md:px-12 print:hidden">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 md:flex-row">
          <div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:gap-6">
            <span className="font-serif text-[13px] uppercase tracking-[0.2em] text-[#2B2722]">
              Lunara Consulting
            </span>
            <span className="text-[13px] tracking-wider">UEN 202534428W</span>
            <a href="tel:+6580539318" className="text-[13px] tracking-wider hover:text-[#2B2722]">
              +65 8053 9318
            </a>
            <a
              href="mailto:nicholas@lunaraconsulting.sg"
              className="text-[13px] tracking-wider hover:text-[#2B2722]"
            >
              nicholas@lunaraconsulting.sg
            </a>
          </div>

          <div className="text-center text-[13px] tracking-wider md:text-right">
            <p>Set in EB Garamond and Lato</p>
            <p className="mt-1">© {new Date().getFullYear()} Lunara Consulting</p>
          </div>
        </div>
      </footer>
    </div>
    </IconContext.Provider>
  );
}
